import React, { useState } from "react";
import { Mic, Settings, BarChart2, FileText, Code, Zap, PlusCircle, Check } from "lucide-react";

const VoiceAgentUI = () => {
  const [activeTab, setActiveTab] = useState("dashboard");

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      {/* Navigation */}
      <nav className="bg-white shadow-sm py-4 px-6">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center space-x-2">
            <Mic className="h-8 w-8 text-purple-600" />
            <span className="text-xl font-bold text-gray-900">Voice Agent by FeedbackbyAI</span>
          </div>
          <div className="flex items-center space-x-4">
            <button className="text-gray-600 hover:text-gray-900">Resources</button>
            <button className="text-gray-600 hover:text-gray-900">Pricing</button>
            <button className="text-gray-600 hover:text-gray-900">Support</button>
            <button className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700">Sign In</button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="bg-gradient-to-r from-purple-700 to-indigo-800 text-white py-12 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="md:w-1/2">
              <h1 className="text-4xl font-bold mb-4">Convert Website Visitors With AI Voice Conversations</h1>
              <p className="text-xl mb-6">Add an AI sales agent to your website that answers questions and drives conversions 24/7.</p>
              <div className="flex flex-col sm:flex-row gap-4">
                <button className="bg-white text-purple-700 px-6 py-3 rounded-lg font-medium hover:bg-gray-100">Start Free Trial</button>
                <button className="bg-transparent border border-white text-white px-6 py-3 rounded-lg font-medium hover:bg-white/10">
                  Watch Demo
                </button>
              </div>
            </div>
            <div className="md:w-1/2 bg-white p-6 rounded-lg shadow-lg">
              <div className="flex items-center justify-between mb-4">
                <span className="text-gray-700 font-medium">yourwebsite.com</span>
                <div className="flex space-x-2">
                  <div className="w-3 h-3 rounded-full bg-red-500"></div>
                  <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                  <div className="w-3 h-3 rounded-full bg-green-500"></div>
                </div>
              </div>
              <div className="h-64 bg-gray-100 rounded flex items-center justify-center relative">
                <span className="text-gray-400">Your Website</span>
                <div className="absolute bottom-4 right-4 bg-purple-600 text-white p-4 rounded-full shadow-lg flex items-center justify-center hover:bg-purple-700 cursor-pointer">
                  <Mic className="h-6 w-6" />
                </div>
                <div className="absolute bottom-16 right-20 bg-white p-4 rounded-lg shadow-lg max-w-xs">
                  <p className="text-sm text-gray-700 mb-2">Hello! I'm your AI assistant. Ask me anything about our products.</p>
                  <div className="flex justify-end">
                    <Mic className="h-4 w-4 text-purple-600" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-16 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12">How It Works</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <div className="bg-purple-100 p-3 rounded-lg inline-block mb-4">
                <FileText className="h-6 w-6 text-purple-600" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Train On Your Content</h3>
              <p className="text-gray-600">
                Upload your website content, PDFs, or documents to train your AI voice agent with your product knowledge.
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <div className="bg-purple-100 p-3 rounded-lg inline-block mb-4">
                <Code className="h-6 w-6 text-purple-600" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Simple Installation</h3>
              <p className="text-gray-600">Add a single line of code to your website to activate your voice agent on your landing pages.</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <div className="bg-purple-100 p-3 rounded-lg inline-block mb-4">
                <Zap className="h-6 w-6 text-purple-600" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Start Converting</h3>
              <p className="text-gray-600">Watch as your AI agent answers visitor questions in real-time and guides them to conversion.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Dashboard Preview */}
      <section className="py-12 px-6 bg-gray-100">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold mb-2 text-center">Dashboard Preview</h2>
          <p className="text-gray-600 text-center mb-12">Customize and monitor your AI voice agent performance</p>

          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <div className="flex border-b">
              <button
                className={`px-6 py-4 font-medium ${
                  activeTab === "dashboard" ? "text-purple-600 border-b-2 border-purple-600" : "text-gray-600"
                }`}
                onClick={() => setActiveTab("dashboard")}
              >
                Dashboard
              </button>
              <button
                className={`px-6 py-4 font-medium ${
                  activeTab === "training" ? "text-purple-600 border-b-2 border-purple-600" : "text-gray-600"
                }`}
                onClick={() => setActiveTab("training")}
              >
                Training
              </button>
              <button
                className={`px-6 py-4 font-medium ${
                  activeTab === "settings" ? "text-purple-600 border-b-2 border-purple-600" : "text-gray-600"
                }`}
                onClick={() => setActiveTab("settings")}
              >
                Settings
              </button>
              <button
                className={`px-6 py-4 font-medium ${
                  activeTab === "analytics" ? "text-purple-600 border-b-2 border-purple-600" : "text-gray-600"
                }`}
                onClick={() => setActiveTab("analytics")}
              >
                Analytics
              </button>
            </div>

            <div className="p-6">
              {activeTab === "dashboard" && (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h4 className="text-sm font-medium text-gray-500 mb-2">Total Conversations</h4>
                      <p className="text-3xl font-bold">1,247</p>
                      <span className="text-green-600 text-sm">+12.5% from last week</span>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h4 className="text-sm font-medium text-gray-500 mb-2">Conversion Rate</h4>
                      <p className="text-3xl font-bold">24.8%</p>
                      <span className="text-green-600 text-sm">+3.2% from last week</span>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h4 className="text-sm font-medium text-gray-500 mb-2">Avg. Conversation Time</h4>
                      <p className="text-3xl font-bold">2m 34s</p>
                      <span className="text-gray-500 text-sm">-10s from last week</span>
                    </div>
                  </div>

                  <div className="bg-gray-50 p-4 rounded-lg h-64 flex items-center justify-center">
                    <BarChart2 className="h-8 w-8 text-gray-400 mr-2" />
                    <span className="text-gray-500">Conversation Analytics Graph</span>
                  </div>

                  <div>
                    <h4 className="font-medium mb-4">Recent Conversations</h4>
                    <div className="space-y-4">
                      {[1, 2, 3].map((i) => (
                        <div key={i} className="border border-gray-200 rounded-lg p-4">
                          <div className="flex justify-between items-start mb-2">
                            <span className="font-medium">Visitor #{742 + i}</span>
                            <span className="text-sm text-gray-500">Today, 2:34 PM</span>
                          </div>
                          <p className="text-gray-600 text-sm mb-2">Asked about pricing plans and features...</p>
                          <div className="flex justify-between items-center">
                            <span className={`text-sm ${i % 2 === 0 ? "text-green-600" : "text-gray-500"}`}>
                              {i % 2 === 0 ? "Converted to lead" : "Ended conversation"}
                            </span>
                            <button className="text-purple-600 text-sm hover:underline">View Details</button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {activeTab === "training" && (
                <div className="space-y-6">
                  <div className="flex justify-between items-center">
                    <h3 className="text-lg font-medium">Training Sources</h3>
                    <button className="flex items-center text-purple-600">
                      <PlusCircle className="h-4 w-4 mr-1" /> Add Source
                    </button>
                  </div>

                  <div className="space-y-4">
                    <div className="border border-gray-200 rounded-lg p-4">
                      <div className="flex justify-between items-start">
                        <div>
                          <h4 className="font-medium">Website Content</h4>
                          <p className="text-sm text-gray-600">yourwebsite.com - 12 pages</p>
                        </div>
                        <div className="bg-green-100 px-2 py-1 rounded flex items-center">
                          <Check className="h-4 w-4 text-green-600 mr-1" />
                          <span className="text-green-600 text-xs">Active</span>
                        </div>
                      </div>
                    </div>

                    <div className="border border-gray-200 rounded-lg p-4">
                      <div className="flex justify-between items-start">
                        <div>
                          <h4 className="font-medium">Product Documentation</h4>
                          <p className="text-sm text-gray-600">5 PDF files</p>
                        </div>
                        <div className="bg-green-100 px-2 py-1 rounded flex items-center">
                          <Check className="h-4 w-4 text-green-600 mr-1" />
                          <span className="text-green-600 text-xs">Active</span>
                        </div>
                      </div>
                    </div>

                    <div className="border border-gray-200 rounded-lg p-4">
                      <div className="flex justify-between items-start">
                        <div>
                          <h4 className="font-medium">Custom Responses</h4>
                          <p className="text-sm text-gray-600">24 custom responses configured</p>
                        </div>
                        <div className="bg-green-100 px-2 py-1 rounded flex items-center">
                          <Check className="h-4 w-4 text-green-600 mr-1" />
                          <span className="text-green-600 text-xs">Active</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === "settings" && (
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-medium mb-4">Voice Agent Settings</h3>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                        <div>
                          <h4 className="font-medium">Voice Type</h4>
                          <p className="text-sm text-gray-600">Choose the voice style for your AI agent</p>
                        </div>
                        <select className="border border-gray-300 rounded-md px-3 py-2">
                          <option>Professional Female</option>
                          <option>Professional Male</option>
                          <option>Casual Female</option>
                          <option>Casual Male</option>
                        </select>
                      </div>

                      <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                        <div>
                          <h4 className="font-medium">Activation Mode</h4>
                          <p className="text-sm text-gray-600">How visitors activate the voice agent</p>
                        </div>
                        <select className="border border-gray-300 rounded-md px-3 py-2">
                          <option>Click to Activate</option>
                          <option>Auto Popup (5 sec)</option>
                          <option>Exit Intent</option>
                        </select>
                      </div>

                      <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                        <div>
                          <h4 className="font-medium">Conversation Style</h4>
                          <p className="text-sm text-gray-600">Personality and tone of the AI agent</p>
                        </div>
                        <select className="border border-gray-300 rounded-md px-3 py-2">
                          <option>Professional</option>
                          <option>Friendly</option>
                          <option>Enthusiastic</option>
                          <option>Concise</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-lg font-medium mb-4">Appearance</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="p-4 border border-gray-200 rounded-lg">
                        <h4 className="font-medium mb-2">Button Color</h4>
                        <div className="flex items-center">
                          <div className="w-8 h-8 bg-purple-600 rounded-full mr-2"></div>
                          <span className="text-sm text-gray-600">#8B5CF6</span>
                        </div>
                      </div>

                      <div className="p-4 border border-gray-200 rounded-lg">
                        <h4 className="font-medium mb-2">Text Color</h4>
                        <div className="flex items-center">
                          <div className="w-8 h-8 bg-gray-900 rounded-full mr-2"></div>
                          <span className="text-sm text-gray-600">#111827</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === "analytics" && (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="bg-gray-50 p-4 rounded-lg h-64 flex items-center justify-center">
                      <BarChart2 className="h-8 w-8 text-gray-400 mr-2" />
                      <span className="text-gray-500">Conversion Rates Over Time</span>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg h-64 flex items-center justify-center">
                      <BarChart2 className="h-8 w-8 text-gray-400 mr-2" />
                      <span className="text-gray-500">Top Conversation Topics</span>
                    </div>
                  </div>

                  <div>
                    <h4 className="font-medium mb-4">Performance by Page</h4>
                    <div className="overflow-hidden border border-gray-200 rounded-lg">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Page</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Conversations
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Conversion Rate
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Avg. Time</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {[
                            { page: "Homepage", convs: 523, rate: "27.8%", time: "2m 12s" },
                            { page: "Pricing", convs: 342, rate: "31.2%", time: "3m 05s" },
                            { page: "Features", convs: 218, rate: "19.5%", time: "1m 47s" },
                          ].map((row, i) => (
                            <tr key={i}>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{row.page}</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{row.convs}</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{row.rate}</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{row.time}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 px-6 bg-purple-700 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to Convert More Visitors?</h2>
          <p className="text-xl mb-8">Add an AI voice agent to your website and start turning conversations into conversions.</p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <button className="bg-white text-purple-700 px-6 py-3 rounded-lg font-medium hover:bg-gray-100">Start 14-Day Free Trial</button>
            <button className="bg-transparent border border-white text-white px-6 py-3 rounded-lg font-medium hover:bg-white/10">
              Schedule Demo
            </button>
          </div>
          <p className="mt-4 text-sm text-purple-200">No credit card required. Cancel anytime.</p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-12 px-6">
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-4 gap-8">
          <div>
            <div className="flex items-center space-x-2 text-white mb-4">
              <Mic className="h-6 w-6" />
              <span className="text-lg font-bold">Voice Agent</span>
            </div>
            <p className="text-sm">Convert website visitors with AI voice conversations. Trained on your content, working 24/7.</p>
          </div>
          <div>
            <h4 className="text-white text-sm font-medium mb-4">Product</h4>
            <ul className="space-y-2 text-sm">
              <li>
                <a href="#" className="hover:text-white">
                  Features
                </a>
              </li>
              <li>
                <a href="#" className="hover:text-white">
                  Pricing
                </a>
              </li>
              <li>
                <a href="#" className="hover:text-white">
                  Case Studies
                </a>
              </li>
              <li>
                <a href="#" className="hover:text-white">
                  Documentation
                </a>
              </li>
            </ul>
          </div>
          <div>
            <h4 className="text-white text-sm font-medium mb-4">Company</h4>
            <ul className="space-y-2 text-sm">
              <li>
                <a href="#" className="hover:text-white">
                  About
                </a>
              </li>
              <li>
                <a href="#" className="hover:text-white">
                  Blog
                </a>
              </li>
              <li>
                <a href="#" className="hover:text-white">
                  Careers
                </a>
              </li>
              <li>
                <a href="#" className="hover:text-white">
                  Contact
                </a>
              </li>
            </ul>
          </div>
          <div>
            <h4 className="text-white text-sm font-medium mb-4">Legal</h4>
            <ul className="space-y-2 text-sm">
              <li>
                <a href="#" className="hover:text-white">
                  Privacy Policy
                </a>
              </li>
              <li>
                <a href="#" className="hover:text-white">
                  Terms of Service
                </a>
              </li>
              <li>
                <a href="#" className="hover:text-white">
                  Cookie Policy
                </a>
              </li>
            </ul>
          </div>
        </div>
        <div className="max-w-6xl mx-auto mt-8 pt-8 border-t border-gray-800 text-sm text-center">
          &copy; 2025 FeedbackbyAI. All rights reserved.
        </div>
      </footer>
    </div>
  );
};

export default VoiceAgentUI;
